import { useEffect, useRef } from 'react';
import { CircleCheck } from 'lucide-react';
import type { CourseUsage } from '@/utils/api-pb';
import { courseClaimPromptText, courseClaimResultMessage } from '@/utils/course-claim';

/** none — no link yet; active — the plan in force; waiting — linked, but a paid plan comes first. */
export type StudentPlanStatus = 'none' | 'active' | 'waiting';

type Lang = 'ru' | 'en';

const text = {
  ru: {
    eyebrow: 'Для учащихся курса ',
    course: 'Больше, чем вайбкодинг',
    title: 'Учебный тариф',
    description: 'Бесплатно, пока действует зачисление на курс. 1 проект, по 1 генерации каждого аналитического инструмента с 2 изменениями результата и 50 сообщений суммарно в чатах ментора и исследований. Лимиты не обновляются ежемесячно.',
    price: '0 ₽',
    activate: 'Активировать доступ',
    learnMore: 'Узнать о курсе',
    badge: { active: 'Активен', waiting: 'Подключён' },
    button: { active: 'Текущий тариф', waiting: 'Доступен после платного тарифа' },
    bannerTitle: { active: 'Учебный тариф активирован', waiting: 'Учебный тариф подключён' },
    bannerText: {
      active: 'Действует, пока вы учитесь на курсе.',
      waiting: 'Включится, когда закончится платный тариф.',
    },
    countedFrom: (date: string) => ` Лимиты считаются с ${date}.`,
    usageTitle: 'Использование учебного пакета',
    editsHint: (edits: number) => `Каждый результат можно изменить ${edits} раза кнопкой «Изменить».`,
    messages: 'Сообщения ментора и исследований',
    of: (used: number, limit: number) => `${used} из ${limit}`,
    artifacts: {
      marketAnalysis: 'Анализ рынка', personas: 'Персоны', positioning: 'Позиционирование',
      businessModel: 'Бизнес-модель', marketingStrategy: 'Маркетинговая стратегия', mvpPlan: 'MVP-план',
      financialModel: 'Финансовая модель',
    } as Record<string, string>,
  },
  en: {
    eyebrow: 'For students of the course ',
    course: 'More Than Vibe Coding',
    title: 'Training plan',
    description: 'Free while your course enrollment is active. One project, one generation per analysis tool with 2 edits of each result, and 50 mentor and research messages combined. Allowances do not reset monthly.',
    price: '$0',
    activate: 'Activate access',
    learnMore: 'Learn about the course',
    badge: { active: 'Active', waiting: 'Linked' },
    button: { active: 'Current plan', waiting: 'Available after your paid plan' },
    bannerTitle: { active: 'Training plan activated', waiting: 'Training plan linked' },
    bannerText: {
      active: 'It stays active while you study on the course.',
      waiting: 'It switches on when your paid plan ends.',
    },
    countedFrom: (date: string) => ` Allowances are counted from ${date}.`,
    usageTitle: 'Training allowance used',
    editsHint: (edits: number) => `Each result can be edited ${edits} times with the Edit button.`,
    messages: 'Mentor and research messages',
    of: (used: number, limit: number) => `${used} of ${limit}`,
    artifacts: {
      marketAnalysis: 'Market analysis', personas: 'Personas', positioning: 'Positioning',
      businessModel: 'Business model', marketingStrategy: 'Marketing strategy', mvpPlan: 'MVP plan',
      financialModel: 'Financial model',
    } as Record<string, string>,
  },
};

function UsageRow({ label, used, limit, lang }: { label: string; used: number; limit: number; lang: Lang }) {
  const percent = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <li>
      <div className="flex items-baseline justify-between gap-[12px] font-segoe text-[14px] leading-[20px]">
        <span className="text-[color:var(--grey-text,#565858)]">{label}</span>
        <span className="font-semibold tabular-nums text-[color:var(--black-text,#0d0d0d)]">{text[lang].of(used, limit)}</span>
      </div>
      <div className="mt-[6px] h-[4px] overflow-hidden rounded-full bg-[var(--grey-accent,#eeeeee)]" aria-hidden="true">
        <div className="h-full rounded-full bg-[var(--main-brand,#2197f1)]" style={{ width: `${percent}%` }} />
      </div>
    </li>
  );
}

/**
 * A claim waiting for the student to agree to it, or null when none is.
 *
 * `account` is shown in the question on purpose: a claim can be posted to the handoff
 * by someone else, and reading whose Creator account is about to be tied to a course
 * account is what lets the student notice that it is not their doing.
 */
export type StudentPlanPrompt = {
  account: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export function StudentPlanCard({ lang, status, grantedAt, usage, claimResult, cabinetUrl, infoUrl, prompt }: {
  lang: Lang;
  status: StudentPlanStatus;
  grantedAt: string | null;
  usage: CourseUsage | null;
  claimResult: string | null;
  cabinetUrl: string;
  infoUrl: string;
  prompt?: StudentPlanPrompt | null;
}) {
  const t = text[lang];
  const cardRef = useRef<HTMLElement>(null);
  const linked = status !== 'none';
  const refused = claimResult !== null && claimResult !== 'active';

  // The card sits below the main plans, so a student returning from the course
  // would otherwise land on the page without seeing that anything happened —
  // including the question that is waiting for them.
  useEffect(() => {
    if (claimResult || prompt) cardRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [claimResult, prompt]);

  const since = grantedAt ? t.countedFrom(new Date(grantedAt).toLocaleDateString(lang === 'en' ? 'en-US' : 'ru-RU')) : '';
  const promptText = courseClaimPromptText(lang, prompt?.account ?? '', linked);

  // Same anatomy as the regular plan cards: white card, grey outline, 16px radius.
  return (
    <section
      ref={cardRef}
      className="mt-[24px] rounded-[16px] border border-[var(--grey-accent,#eeeeee)] bg-[var(--white,#ffffff)] p-[24px]"
      aria-label={t.title}
    >
      <div className="flex flex-col gap-[24px] md:flex-row md:items-start md:justify-between">
        <div className="max-w-[690px]">
          <p className="font-segoe text-[14px] leading-[20px] text-[color:var(--main-brand,#2197f1)]">
            {t.eyebrow}
            <a href={infoUrl} target="_blank" rel="noreferrer" className="underline hover:no-underline">{t.course}</a>
          </p>
          <div className="mt-[4px] flex flex-wrap items-center gap-[12px]">
            <h2 className="font-manrope text-[24px] font-bold leading-[32px] text-[color:var(--black-text,#0d0d0d)]">{t.title}</h2>
            {linked && (
              <span className="inline-flex items-center gap-[4px] rounded-[16px] bg-[var(--accent-background,rgba(33,151,241,0.1))] px-[8px] py-[4px] font-manrope text-[12px] font-semibold leading-[16px] text-[color:var(--main-brand,#2197f1)]">
                <CircleCheck size={14} aria-hidden="true" />
                {t.badge[status]}
              </span>
            )}
          </div>
          <p className="mt-[12px] font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)]">{t.description}</p>
        </div>
        <div className="flex w-full flex-col gap-[8px] md:w-[240px] md:shrink-0">
          <span className="font-manrope text-[32px] font-bold leading-[40px] text-[color:var(--black-text,#0d0d0d)]">{t.price}</span>
          {linked
            ? <span className="flex h-[48px] items-center justify-center rounded-[16px] bg-[var(--grey-accent,#eeeeee)] px-[16px] text-center font-segoe text-[14px] font-semibold leading-[20px] text-[color:var(--grey-text,#565858)]">{t.button[status]}</span>
            : <a href={`${cabinetUrl}/lessons/11`} className="flex h-[48px] items-center justify-center rounded-[16px] bg-[var(--main-brand,#2197f1)] px-[16px] text-center font-segoe text-[16px] font-semibold leading-[24px] text-[color:var(--white,#ffffff)] transition-opacity hover:opacity-90">{t.activate}</a>}
          <a href={infoUrl} target="_blank" rel="noreferrer" className="flex h-[48px] items-center justify-center rounded-[16px] border border-[var(--grey-accent,#eeeeee)] bg-[var(--white,#ffffff)] px-[16px] text-center font-segoe text-[16px] font-semibold leading-[24px] text-[color:var(--black-text,#0d0d0d)] transition-colors hover:bg-[var(--grey-background,#f8f8f8)]">{t.learnMore}</a>
        </div>
      </div>

      {/*
        Hidden while a question is on screen: "the plan is connected" directly above
        "connect the plan?" reads as a contradiction, and the student cannot tell which
        of the two is true. The badge and the allowance stay — those are facts about the
        account, not answers to the question. The banner returns once they have answered.
      */}
      {linked && !prompt && (
        <div className="mt-[24px] flex items-start gap-[12px] rounded-[16px] bg-[var(--grey-background,#f8f8f8)] p-[16px]" role="status">
          <CircleCheck className="mt-[2px] shrink-0 text-[color:var(--main-brand,#2197f1)]" size={20} aria-hidden="true" />
          <div className="font-segoe">
            <p className="text-[16px] font-semibold leading-[24px] text-[color:var(--black-text,#0d0d0d)]">{t.bannerTitle[status]}</p>
            <p className="text-[14px] leading-[20px] text-[color:var(--grey-text,#565858)]">{t.bannerText[status]}{since}</p>
          </div>
        </div>
      )}

      {prompt && (
        <div
          className="mt-[24px] rounded-[16px] border border-[var(--main-brand,#2197f1)] p-[16px]"
          role="group"
          aria-label={promptText.question}
        >
          <p className="font-segoe text-[16px] font-semibold leading-[24px] text-[color:var(--black-text,#0d0d0d)]">
            {promptText.question}
          </p>
          <p className="mt-[4px] font-segoe text-[14px] leading-[20px] text-[color:var(--grey-text,#565858)]">
            {promptText.note}
          </p>
          <div className="mt-[16px] flex flex-col gap-[8px] sm:flex-row">
            <button
              type="button"
              onClick={prompt.onConfirm}
              disabled={prompt.busy}
              className="flex h-[48px] items-center justify-center rounded-[16px] bg-[var(--main-brand,#2197f1)] px-[24px] font-segoe text-[16px] font-semibold leading-[24px] text-[color:var(--white,#ffffff)] transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {promptText.confirm}
            </button>
            <button
              type="button"
              onClick={prompt.onCancel}
              disabled={prompt.busy}
              className="flex h-[48px] items-center justify-center rounded-[16px] border border-[var(--grey-accent,#eeeeee)] bg-[var(--white,#ffffff)] px-[24px] font-segoe text-[16px] font-semibold leading-[24px] text-[color:var(--black-text,#0d0d0d)] transition-colors hover:bg-[var(--grey-background,#f8f8f8)] disabled:opacity-60"
            >
              {promptText.cancel}
            </button>
          </div>
        </div>
      )}

      {refused && (
        <p className="mt-[24px] rounded-[16px] border border-[var(--error-border,#ff9196)] p-[16px] font-segoe text-[14px] leading-[20px] text-[color:var(--black-text,#0d0d0d)]" role="alert">
          {courseClaimResultMessage(claimResult, lang)}
        </p>
      )}

      {linked && usage && (
        <div className="mt-[24px]">
          <h3 className="font-manrope text-[16px] font-bold leading-[24px] text-[color:var(--black-text,#0d0d0d)]">{t.usageTitle}</h3>
          {usage.editsPerArtifact ? <p className="mt-[4px] font-segoe text-[14px] leading-[20px] text-[color:var(--grey-text,#565858)]">{t.editsHint(usage.editsPerArtifact)}</p> : null}
          <ul className="mt-[12px] grid gap-[16px] md:grid-cols-2 md:gap-x-[32px]">
            <UsageRow label={t.messages} used={usage.messages.used} limit={usage.messages.limit} lang={lang} />
            {Object.entries(usage.artifacts).map(([key, item]) => (
              <UsageRow key={key} label={t.artifacts[key] ?? key} used={item.used} limit={item.limit} lang={lang} />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
